import { ItemTypes } from '../constants/constants';

/*
 * Réponses aux questions des sections liées, gardées à travers un rechargement
 * (cf. reloadLinkedSections). Recharger remplace chaque zone par des box neuves :
 * une box injectée a pour id `${idDansLaSection}-${idDeLaBoxQuiInjecte}-${horodatage}`
 * (mutateInjectSection), chaîné à chaque niveau jusqu'à l'ancre de la section
 * liée, dont l'id est conservé (copie réinsérée). Sans les horodatages, l'id
 * décrit donc le chemin de la box dans la bibliothèque : même question, même
 * clé, avant et après rechargement.
 */

// Horodatages de mutateInjectSection et des copies de lignes récursives
// (`-row{n}-{horodatage}`) : seuls segments à 13 chiffres, les ids de box
// étant des uuid en base 36.
const TIMESTAMP_SEGMENT = /-\d{13,}(?=-|$)/g;

export const linkedSectionBoxKey = (box) => box.id.replace(TIMESTAMP_SEGMENT, '');

/** La box a été injectée par l'une de ces ancres, à n'importe quelle profondeur. */
export const isInLinkedSectionAnchors = (box, anchorIds) => {
  if (!box.boxSectionId) return false;
  const key = linkedSectionBoxKey(box);
  return anchorIds.some((anchorId) =>
    key.endsWith(`-${anchorId.replace(TIMESTAMP_SEGMENT, '')}`)
  );
};

export const zonesAnchorIds = (groups) =>
  groups.flatMap(({ anchors }) => anchors.map(({ anchorId }) => anchorId));

const SELECTIONS = {
  [ItemTypes.QUESTION]: { arrayKey: 'checkboxs', selectionKey: 'checked' },
  [ItemTypes.CHECKBOXS_VARIABLE]: { arrayKey: 'checkboxs', selectionKey: 'checked' },
  [ItemTypes.SELECTOR_VARIABLE]: { arrayKey: 'options', selectionKey: 'selected' },
};

const labelText = (label) =>
  typeof label === 'string' ? label : label?.getCurrentContent?.().getPlainText() ?? null;

const choice = ({ id, label }) => ({ id, label: labelText(label) });

const answerOf = (box) => {
  const selection = SELECTIONS[box.type];
  if (selection) {
    const selected = (box[selection.arrayKey] ?? []).filter((item) => item[selection.selectionKey]);
    return selected.length ? { selected: selected.map(choice) } : null;
  }
  if (box.type === ItemTypes.SECTION_VARIABLE && box.answer) {
    const question = box.questions?.find(({ id }) => id === box.answer);
    return question ? { answer: choice(question) } : null;
  }
  return null;
};

// Une même clé ne se répète que si une section est injectée deux fois au même
// endroit : l'ordre des box départage.
const withOccurrences = (boxes) => {
  const seen = {};
  return boxes.map((box) => {
    const key = linkedSectionBoxKey(box);
    seen[key] = (seen[key] ?? 0) + 1;
    return { box, key: `${key}#${seen[key]}` };
  });
};

/**
 * Réponses données dans les zones rechargées. Une réponse posée par un trigger
 * n'est pas relevée : le trigger la repose de lui-même.
 */
export const captureLinkedSectionAnswers = (boxes, anchorIds) =>
  withOccurrences(boxes.filter((box) => isInLinkedSectionAnchors(box, anchorIds)))
    .map(({ box, key }) => ({
      key,
      type: box.type,
      ...(!box.isActivatedByTrigger && answerOf(box)),
    }))
    .filter((entry) => entry.selected || entry.answer);

/** Questions des zones, par clé, dans le contenu courant. */
export const indexLinkedSectionQuestions = (boxes, anchorIds) =>
  new Map(
    withOccurrences(boxes.filter((box) => isInLinkedSectionAnchors(box, anchorIds))).map(
      ({ box, key }) => [key, box]
    )
  );

// Option d'origine retrouvée par son id, sinon par son libellé s'il est unique
// (option recréée dans la bibliothèque sous le même nom).
const findChoice = (items = [], { id, label }) => {
  const byId = items.find((item) => item.id === id);
  if (byId) return byId;
  if (!label) return null;
  const byLabel = items.filter((item) => labelText(item.label) === label);
  return byLabel.length === 1 ? byLabel[0] : null;
};

/**
 * Ce qu'il faut faire pour rendre à `box` la réponse relevée :
 * - { status: 'same' } : elle l'a déjà ;
 * - { status: 'missing' } : une option choisie n'existe plus, la question reste telle quelle ;
 * - { status: 'select', ids } : options à cocher (et seulement elles) ;
 * - { status: 'answer', optionId, label } : réponse Oui/Non d'une section variable.
 */
export const planAnswerRestore = (entry, box) => {
  if (box.type !== entry.type) return { status: 'missing' };
  const selection = SELECTIONS[box.type];
  if (selection) {
    const items = box[selection.arrayKey] ?? [];
    const targets = entry.selected.map((c) => findChoice(items, c));
    if (targets.some((t) => !t)) return { status: 'missing' };
    const ids = targets.map(({ id }) => id);
    const current = items.filter((item) => item[selection.selectionKey]).map(({ id }) => id);
    const same = current.length === ids.length && ids.every((id) => current.includes(id));
    return same ? { status: 'same' } : { status: 'select', ids };
  }
  const question = findChoice(box.questions, entry.answer);
  if (!question || (question.label === 'Oui' && !box.sectionId)) return { status: 'missing' };
  if (box.answer === question.id) return { status: 'same' };
  return { status: 'answer', optionId: question.id, label: question.label };
};

/** Réponse cochée comme le ferait l'utilisateur, à passer à updateBox. */
export const selectAnswer = (ids) => (box) => {
  const { arrayKey, selectionKey } = SELECTIONS[box.type];
  box[arrayKey] = box[arrayKey].map((item) => ({ ...item, [selectionKey]: ids.includes(item.id) }));
};
